// src/components/SidebarTiles.jsx
import React from "react";

const SidebarTiles = (props) => {
  const {
    sidebarTiles,
    pinnedId,
    setPinnedId,
    activeSpeakerId,
    remoteMediaState,
    remoteVideoRefs,
    toggleMyMic,
    toggleMyCam,
    isMicOn,
    isCamOn,
    toggleRemoteTileAudio,
    toggleRemoteTileVideo,
    isObserver,
    isAdminViewer,
  } = props;

  const observerLike = !!(isObserver || isAdminViewer);

  const attachVideoRef = (participant) => (el) => {
    if (!el) return;

    const { id, isLocal, stream } = participant;
    if (!stream) return;

    if (isLocal) {
      // Local tile video – just mirror the stream
      if (el.srcObject !== stream) {
        el.srcObject = stream;
      }
    } else {
      // Remote tile
      remoteVideoRefs.current[id] = el;
      if (el.srcObject !== stream) {
        el.srcObject = stream;
      }
    }

    el
      .play()
      .catch((err) =>
        console.warn("[SIDEBAR] Error playing tile video:", err?.message || err)
      );
  };

  if (!sidebarTiles || sidebarTiles.length === 0) {
    return null;
  }

  return (
    <div className="room-videosRow">
      <div className="room-videoHeader">
        <div className="room-videoTitle">Other participants</div>
        <div className="room-videoSubTitle">
          Click a tile to bring it to the main view.
        </div>
      </div>

      <div className="room-remoteGrid">
        {sidebarTiles.map((p) => {
          const isPinned = pinnedId === p.id;
          const isActive = activeSpeakerId === p.id;
          const label = p.isLocal ? "You" : p.email || p.id;

          const perMedia = remoteMediaState[p.id] || {
            audioOn: true,
            videoOn: true,
          };

          const handleTileClick = () => {
            setPinnedId(isPinned ? null : p.id);
          };

          const handleLocalMicClick = (e) => {
            e.stopPropagation();
            toggleMyMic();
          };

          const handleLocalCamClick = (e) => {
            e.stopPropagation();
            toggleMyCam();
          };

          const handleRemoteAudioClick = (e) => {
            e.stopPropagation();
            toggleRemoteTileAudio(p.id);
          };

          const handleRemoteVideoClick = (e) => {
            e.stopPropagation();
            toggleRemoteTileVideo(p.id);
          };

          return (
            <div key={p.id} className="room-videoCard">
              {/* Tile header */}
              <div className="room-videoHeader">
                <div>
                  <div className="room-videoTitle">{label}</div>
                  <div className="room-videoSubTitle">
                    {p.isLocal ? "Local preview" : "Remote participant"}
                  </div>
                </div>
                <button
                  type="button"
                  className={
                    "room-pinBtn" + (isPinned ? " room-pinBtn--active" : "")
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTileClick();
                  }}
                >
                  📌
                </button>
              </div>

              {/* Clickable surface – swaps with main on click */}
              <div
                className={
                  "room-videoSurface remote-surface" +
                  (isActive ? " speaker-ring" : "")
                }
                onClick={handleTileClick}
              >
                {p.stream ? (
                  <video
                    ref={attachVideoRef(p)}
                    className={
                      "room-videoElement" +
                      (p.isLocal ? " room-videoElement--local" : "")
                    }
                    playsInline
                    autoPlay
                    muted={true} // keep tiles muted to avoid echo
                  />
                ) : (
                  <div className="room-videoSurface--empty">
                    Waiting for video…
                  </div>
                )}

                {/* 🔇 Overlay controls on hover only (wired to .remote-controls) */}
                {!observerLike && (
                  <div className="room-overlayControls remote-controls">
                    {p.isLocal ? (
                      <>
                        <button
                          type="button"
                          className={
                            "room-iconBtn" +
                            (isMicOn ? " room-iconBtn--active" : "")
                          }
                          onClick={handleLocalMicClick}
                          title={isMicOn ? "Mute your mic" : "Unmute your mic"}
                        >
                          {isMicOn ? "🎙" : "🔇"}
                        </button>
                        <button
                          type="button"
                          className={
                            "room-iconBtn" +
                            (isCamOn ? " room-iconBtn--active" : "")
                          }
                          onClick={handleLocalCamClick}
                          title={
                            isCamOn ? "Turn off your camera" : "Turn on camera"
                          }
                        >
                          {isCamOn ? "📷" : "🚫"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="room-iconBtn"
                          onClick={handleRemoteAudioClick}
                          title={
                            perMedia.audioOn
                              ? "Mute this participant"
                              : "Unmute this participant"
                          }
                        >
                          {perMedia.audioOn ? "🔊" : "🔇"}
                        </button>
                        <button
                          type="button"
                          className="room-iconBtn"
                          onClick={handleRemoteVideoClick}
                          title={
                            perMedia.videoOn
                              ? "Hide this video"
                              : "Show this video"
                          }
                        >
                          {perMedia.videoOn ? "📹" : "🚫"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarTiles;
